import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  AlertTriangle, Camera, CheckCircle2, ChevronDown, ChevronUp, Loader2, Plus, Send, Trash2, X,
} from "lucide-react";
import {
  DELAY_LABEL, REPORT_STATUS, STATUS_LABEL, TASK_STATUSES, clock, duration, fmtDate, minutesLabel, todayISO, uploadPhoto, useAuthImage,
} from "@/lib/dwr";
import { MaterialsList } from "@/components/dwr/TaskMaterials";
import CustomerSelect from "@/components/CustomerSelect";

const newLine = () => ({
  id: crypto.randomUUID?.() || String(Math.random()).slice(2), task_id: null, task_no: "", title: "", customer: "",
  location: "", status: "completed", description: "", result: "", challenges: "", materials_used: "", photos: [],
  start_time: "", end_time: "", delay_reason: null, delay_responsible: "", follow_up_required: false,
  follow_up_date: null, customer_confirmation: "", remarks: "",
});

// Mirrors the server's submit checks so people see problems before sending.
function problems(entries) {
  if (!entries.length) return ["Add at least one line describing your work today."];
  const out = [];
  entries.forEach((e, i) => {
    const n = `Line ${i + 1}`;
    if (!e.title?.trim()) out.push(`${n}: what work or task was this?`);
    if (!e.description?.trim() && !e.auto?.arrived_at) out.push(`${n}: describe the work done.`);
    if (e.status === "delayed" && (!e.delay_reason || !e.delay_responsible?.trim())) out.push(`${n}: delayed work needs a reason and who acts next.`);
    if (e.follow_up_required && !e.follow_up_date) out.push(`${n}: choose the follow-up date.`);
    if (e.start_time && e.end_time && e.end_time < e.start_time) out.push(`${n}: end time is before start time.`);
  });
  return out;
}

function Photo({ url, onRemove }) {
  const src = useAuthImage(url);
  return (
    <div className="relative w-20 h-20 rounded-lg overflow-hidden border border-border bg-muted">
      {src ? <img src={src} alt="Work photo" className="w-full h-full object-cover" /> : <Loader2 className="w-4 h-4 animate-spin m-auto mt-8 text-muted-foreground" />}
      {onRemove && (
        <button type="button" onClick={onRemove} aria-label="Remove photo"
          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center">
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function Line({ index, line, editable, onChange, onRemove }) {
  const [open, setOpen] = useState(!!(line.result || line.challenges || line.materials_used || line.follow_up_required || line.remarks || line.customer_confirmation));
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (patch) => onChange({ ...line, ...patch });

  const addPhotos = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of Array.from(files).slice(0, 12 - line.photos.length)) urls.push(await uploadPhoto(f));
      set({ photos: [...line.photos, ...urls] });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Card className="p-4 rounded-xl border border-border bg-card space-y-3" data-testid={`dwr-line-${index}`}>
      <div className="flex items-start gap-2">
        <span className="mt-2 text-xs font-mono text-muted-foreground w-5 shrink-0">{index + 1}</span>
        <div className="flex-1 min-w-0 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_12rem] gap-2">
            <div className="space-y-1">
              <Input value={line.title} disabled={!editable} placeholder="Work / task done"
                onChange={(e) => set({ title: e.target.value })} data-testid={`dwr-title-${index}`} />
              {line.task_no && <p className="text-[11px] text-muted-foreground">Linked to task {line.task_no}: your status here updates it.</p>}
            </div>
            <Select value={line.status} disabled={!editable} onValueChange={(v) => set({ status: v })}>
              <SelectTrigger data-testid={`dwr-status-${index}`}><SelectValue /></SelectTrigger>
              <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {line.auto?.arrived_at && (
            <div className="rounded-lg bg-muted/50 p-3 space-y-2 text-sm">
              <p>
                Arrived {clock(line.auto.arrived_at)}
                {line.auto.completed_at ? ` → completed ${clock(line.auto.completed_at)}` : " · still working"}
                {line.auto.duration_minutes !== null && line.auto.duration_minutes !== undefined && <span className="text-muted-foreground"> · {minutesLabel(line.auto.duration_minutes)}</span>}
              </p>
              {line.auto.materials?.length > 0 && <MaterialsList materials={line.auto.materials} />}
            </div>
          )}

          <Textarea rows={3} value={line.description} disabled={!editable} placeholder={line.auto?.arrived_at ? "Notes (optional)" : "What did you do?"}
            onChange={(e) => set({ description: e.target.value })} data-testid={`dwr-description-${index}`} />

          {line.status === "delayed" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 rounded-lg bg-rose-500/5 border border-rose-500/30">
              <div className="space-y-1">
                <Label className="text-xs">Why is it delayed? *</Label>
                <Select value={line.delay_reason || ""} disabled={!editable} onValueChange={(v) => set({ delay_reason: v })}>
                  <SelectTrigger><SelectValue placeholder="Choose a reason" /></SelectTrigger>
                  <SelectContent>{Object.entries(DELAY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Who must act next? *</Label>
                <Input value={line.delay_responsible} disabled={!editable} placeholder="e.g. Customer IT, Stores, supplier"
                  onChange={(e) => set({ delay_responsible: e.target.value })} />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="col-span-2">
              <CustomerSelect value={line.customer} disabled={!editable || !!line.task_id} onChange={(v) => set({ customer: v })} />
            </div>
            <Input value={line.location} disabled={!editable} placeholder="Location" onChange={(e) => set({ location: e.target.value })} className="col-span-2" />
            <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Start</Label>
              <Input type="time" value={line.start_time} disabled={!editable} onChange={(e) => set({ start_time: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">End</Label>
              <Input type="time" value={line.end_time} disabled={!editable} onChange={(e) => set({ end_time: e.target.value })} /></div>
            <p className="col-span-2 self-end text-xs text-muted-foreground pb-2">{duration(line.start_time, line.end_time)}</p>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            {line.photos.map((p, i) => (
              <Photo key={p} url={p} onRemove={editable ? () => set({ photos: line.photos.filter((_, j) => j !== i) }) : null} />
            ))}
            {editable && line.photos.length < 12 && (
              <>
                <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple hidden
                  onChange={(e) => addPhotos(e.target.files)} />
                <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid={`dwr-photo-${index}`}>
                  {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Camera className="w-4 h-4 mr-1" />} Photo
                </Button>
              </>
            )}
          </div>

          <button type="button" className="text-xs font-medium text-primary inline-flex items-center gap-1" onClick={() => setOpen(!open)}>
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} More details
          </button>
          {open && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Textarea rows={2} value={line.result} disabled={!editable} placeholder="Result / outcome" onChange={(e) => set({ result: e.target.value })} />
              <Textarea rows={2} value={line.challenges} disabled={!editable} placeholder="Challenges" onChange={(e) => set({ challenges: e.target.value })} />
              <Textarea rows={2} value={line.materials_used} disabled={!editable} placeholder="Materials / equipment used" onChange={(e) => set({ materials_used: e.target.value })} />
              <Textarea rows={2} value={line.remarks} disabled={!editable} placeholder="Remarks" onChange={(e) => set({ remarks: e.target.value })} />
              <Input value={line.customer_confirmation} disabled={!editable} placeholder="Confirmed by (customer name)" onChange={(e) => set({ customer_confirmation: e.target.value })} />
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={line.follow_up_required} disabled={!editable} onCheckedChange={(v) => set({ follow_up_required: !!v, follow_up_date: v ? line.follow_up_date : null })} />
                  Follow-up needed
                </label>
                {line.follow_up_required && (
                  <Input type="date" className="w-auto" value={line.follow_up_date || ""} disabled={!editable} onChange={(e) => set({ follow_up_date: e.target.value })} />
                )}
              </div>
            </div>
          )}
        </div>
        {editable && (
          <Button type="button" variant="ghost" size="icon" onClick={onRemove} aria-label="Remove line"><Trash2 className="w-4 h-4" /></Button>
        )}
      </div>
    </Card>
  );
}

export default function MyDay() {
  const { dwr } = useAuth();
  const [day, setDay] = useState(todayISO());
  const [report, setReport] = useState(null);
  const [entries, setEntries] = useState([]);
  const [saveState, setSaveState] = useState("saved"); // saved | dirty | saving | error
  const [submitting, setSubmitting] = useState(false);
  const [myTasks, setMyTasks] = useState([]);
  const dirty = useRef(false);

  const load = useCallback(async (d) => {
    setReport(null);
    try {
      const { data } = await api.get("/dwr/reports/mine", { params: { date: d } });
      setReport(data.report);
      setEntries(data.report.entries || []);
      // Pre-filled lines aren't saved until the person actually edits something.
      dirty.current = false;
      setSaveState("saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(day); }, [day, load]);
  useEffect(() => {
    api.get("/dwr/tasks", { params: { scope: "mine" } }).then(({ data }) => setMyTasks(data.items || [])).catch(() => {});
  }, []);

  const editable = report && ["draft", "returned"].includes(report.status);

  // Autosave 1.5s after the last change: a dropped connection in the field
  // never costs more than the last few keystrokes.
  useEffect(() => {
    if (!editable || !dirty.current) return undefined;
    setSaveState("dirty");
    const t = setTimeout(async () => {
      setSaveState("saving");
      try {
        const { data } = await api.put(`/dwr/reports/mine/${day}`, { entries });
        setReport(data.report);
        dirty.current = false;
        setSaveState("saved");
      } catch {
        setSaveState("error");
      }
    }, 1500);
    return () => clearTimeout(t);
  }, [entries, day, editable]);

  const change = (next) => { dirty.current = true; setEntries(next); };
  const openTasks = useMemo(
    () => myTasks.filter((t) => !["completed", "cancelled"].includes(t.status) && !entries.some((e) => e.task_id === t.id)),
    [myTasks, entries],
  );
  const addFromTask = (taskId) => {
    const t = myTasks.find((x) => x.id === taskId);
    if (t) change([...entries, { ...newLine(), task_id: t.id, task_no: t.task_no, title: t.title, customer: t.customer || "", location: t.location || "", status: t.status === "pending" ? "in_progress" : t.status }]);
  };

  const submit = async () => {
    const issues = problems(entries);
    if (issues.length) { toast.error(issues[0]); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/dwr/reports/mine/${day}/submit`, { entries });
      setReport(data.report);
      setEntries(data.report.entries);
      dirty.current = false;
      toast.success(dwr?.supervisor ? `Sent to ${dwr.supervisor.name} for review` : "Report submitted");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const status = report ? REPORT_STATUS[report.status] || REPORT_STATUS.draft : null;
  const lastReturn = [...(report?.review_history || [])].reverse().find((h) => h.action === "return");
  const lastApprove = [...(report?.review_history || [])].reverse().find((h) => h.action === "approve");

  return (
    <div className="space-y-5 max-w-4xl" data-testid="my-day-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">My Day</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {fmtDate(day)}{dwr?.supervisor ? ` · reviewed by ${dwr.supervisor.name}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" value={day} max={todayISO()} onChange={(e) => e.target.value && setDay(e.target.value)} className="w-auto" data-testid="dwr-date" />
          {status && <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${status.tone}`}>{status.label}</span>}
        </div>
      </div>

      {dwr && !dwr.expected_to_report && (
        <p className="text-sm text-muted-foreground">You don't have a supervisor set, so nobody is waiting for this report. You can still use it as your work log.</p>
      )}
      {report?.status === "returned" && lastReturn && (
        <div className="flex gap-3 p-4 rounded-xl border border-rose-500/30 bg-rose-500/5" data-testid="dwr-returned-banner">
          <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
          <div><p className="font-medium">Returned for correction</p><p className="text-sm text-muted-foreground mt-0.5">“{lastReturn.comment}”</p></div>
        </div>
      )}
      {report?.status === "approved" && (
        <div className="flex gap-3 p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          <p className="text-sm">Approved{lastApprove?.comment ? `: “${lastApprove.comment}”` : "."}</p>
        </div>
      )}
      {report?.status === "submitted" && (
        <p className="text-sm text-muted-foreground">Submitted. It can be changed again only if your supervisor returns it for correction.</p>
      )}

      {!report ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <>
          {entries.length === 0 && editable && (
            <Card className="p-6 text-center border-dashed">
              <p className="font-medium">Nothing here yet</p>
              <p className="text-sm text-muted-foreground mt-1">Add each piece of work you did today, or start from one of your tasks.</p>
            </Card>
          )}
          <div className="space-y-3">
            {entries.map((line, i) => (
              <Line key={line.id} index={i} line={line} editable={editable}
                onChange={(l) => change(entries.map((x, j) => (j === i ? l : x)))}
                onRemove={() => change(entries.filter((_, j) => j !== i))} />
            ))}
          </div>

          {editable && (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={() => change([...entries, newLine()])} data-testid="dwr-add-line">
                <Plus className="w-4 h-4 mr-1" /> Add work
              </Button>
              {openTasks.length > 0 && (
                <Select value="" onValueChange={addFromTask}>
                  <SelectTrigger className="w-auto" data-testid="dwr-add-from-task"><SelectValue placeholder="+ From my tasks" /></SelectTrigger>
                  <SelectContent>{openTasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.task_no} · {t.title}</SelectItem>)}</SelectContent>
                </Select>
              )}
              <div className="flex-1" />
              <span className="text-xs text-muted-foreground" data-testid="dwr-save-state">
                {saveState === "saving" ? "Saving…" : saveState === "dirty" ? "Unsaved changes" : saveState === "error" ? "Not saved: check your connection" : "Draft saved"}
              </span>
              <Button onClick={submit} disabled={submitting} className="kn-gradient-bg text-white border-0" data-testid="dwr-submit">
                {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                {report.status === "returned" ? "Resubmit day" : "Submit day"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
